import {
  ContentSections,
  getItemReference,
  loadContentSections,
} from "@tiicker/util/lib/contentful/helpers";
import {
  BrandDetailDocument,
  ContentfulContentType,
  ContentfulKey,
  GenericMarketingPage,
  InsightDocument,
  PageSection,
  PageSectionKey,
  PerkDocument,
  BrandInvestment,
} from "@tiicker/util/lib/contentful/types";
import { GetIexCompany } from "$gql/queries/general/GetIexCompany.gen";
import React from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import { Entry, EntryCollection } from "contentful";
import { IexCompany } from "$gql/types.gen";
import { useRouter } from "next/router";
import { buildGraphqlClient } from "$gql/client";
import { compact } from "lodash";
import {
  InsightsAssociatedWithBrand,
  PerksForBrand,
  PerksForOwningBrand,
} from "@tiicker/util/lib/contentful/queries";
import LoadingBlock from "components/Loading/LoadingBlock";
import BrandPageUI from "components/BrandPage/BrandPageUI/BrandPageUI";
import { GetIexQuote } from "$gql/queries/currentPricing/GetIexQuote.gen";

export enum TabKey {
  Perks = "Perks",
  HowItWorks = "How it works",
  About = "About",
  Insights = "Insights",
  News = "News",
  Performance = "Performance",
  ShareholderStore = "Shareholder Store",
}

type Props = {
  symbol?: string;
  brandDetail?: EntryCollection<BrandDetailDocument>;
  iexCompany: IexCompany | null;
  joinNowCTA?: Entry<PageSection>;
  howItWorks?: Entry<PageSection>;
  tiickerPerksCTA?: Entry<PageSection>;
  redirected?: boolean;
  contentSections?: ContentSections;
  pageSectionIncludes?: any;
  brandPerks?: EntryCollection<PerkDocument>;
  owningPerks?: EntryCollection<PerkDocument>;
  brandInsights?: EntryCollection<InsightDocument>;
  brandInvestment?: Entry<BrandInvestment>;
};

const BrandDetailTabs = (props: Props) => {
  const router = useRouter();
  const { brand } = router.query;

  // Add state for quote data
  const [quote, setQuote] = React.useState(props.iexCompany?.quote || null);

  // Add effect to fetch quote data on client-side
  React.useEffect(() => {
    const fetchQuote = async () => {
      if (!props.symbol) return;

      const graphqlClient = buildGraphqlClient({
        baseURL: process.env.API_URL,
      });
      try {
        const result = await graphqlClient.query<
          GetIexQuote.Query,
          GetIexQuote.Variables
        >({
          query: GetIexQuote.Document,
          variables: {
            ticker: props.symbol,
          },
        });

        if (result.data.iex.quote) {
          setQuote(result.data.iex.quote);
          console.log("Updated quote data:", result.data.iex.quote);
        }
      } catch (error) {
        console.error("Error fetching quote:", error);
      }
    };

    fetchQuote();

    // Set up polling for real-time updates if needed
    const interval = setInterval(fetchQuote, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [props.symbol]); // Change dependency to props.symbol

  if (brand) {
    localStorage.setItem("brand", brand as string);
  }

  if (router.isFallback) {
    return <LoadingBlock />;
  }

  const canonicalUrl = `https://www.tiicker.com/brand/${brand as string}`;

  return (
    <>
      <link rel="canonical" href={canonicalUrl} key="canonical" />
      <BrandPageUI {...props} quote={quote || undefined} />
    </>
  );
};

export const defaultBrandTab = TabKey.Perks;

export const getStaticPaths: GetStaticPaths = async () => {
  // const graphqlClient = buildGraphqlClient({ baseURL: process.env.API_URL });

  // try {
  //   const allBrands = await graphqlClient.query<
  //     GetAllBrands.Query,
  //     GetAllBrands.Variables
  //   >({
  //     query: GetAllBrands.Document,
  //   });

  //   const paths = allBrands.data.getAllBrands.map((b) => `/brand/${b.ticker}`);
  //   return { paths, fallback: true };
  // } catch (e) {
  //   console.error("Error getting static paths for brand pages", e);
  return { paths: [], fallback: true };
  // }
};

export const getStaticProps = async ({ params }) => {
  const symbol =
    params && !Array.isArray(params.symbol) && params.symbol
      ? params.symbol
      : "";

  if (symbol !== symbol.toUpperCase()) {
    return {
      redirect: {
        destination: `/brand/${symbol.toUpperCase()}`,
      },
      props: {
        iexCompany: null,
      },
    };
  }

  if (symbol.toLowerCase() === "tiicker") {
    return {
      redirect: {
        destination: "/tiicker-perks",
      },
      props: {
        iexCompany: null,
      },
    };
  }

  const redirect =
    params && !Array.isArray(params.redirect) && params.redirect !== undefined
      ? params.redirect.trim() === "true"
      : false;

  const client = getContentfulClient("contentful");

  const brandDetail = await client.getEntries<BrandDetailDocument>({
    content_type: ContentfulContentType.BrandDetail,
    "fields.tickerSymbol": symbol.toUpperCase(),
    limit: 1,
    include: 2,
  });
  const brandPerks = await client.getEntries<PerkDocument>(
    PerksForBrand(symbol)
  );
  const owningPerks = await client.getEntries<PerkDocument>(
    PerksForOwningBrand(symbol)
  );

  // Fetch all brand investments
  const brandInvestments = await client.getEntries<BrandInvestment>({
    content_type: ContentfulContentType.BrandInvestment,
    include: 3,
  });

  // Filter to find the matching brand investment
  const matchingBrandInvestment = brandInvestments.items.find((item) =>
    item.fields.brandReference?.some(
      (ref) => ref.fields?.tickerSymbol === symbol.toUpperCase()
    )
  );

  const graphqlClient = buildGraphqlClient({ baseURL: process.env.API_URL });

  const iexCompany = await graphqlClient.query<
    GetIexCompany.Query,
    GetIexCompany.Variables
  >({
    query: GetIexCompany.Document,
    variables: {
      ticker:
        brandDetail.items.length > 0
          ? brandDetail.items[0].fields.iexSymbol ?? symbol.toLowerCase()
          : symbol.toLowerCase(),
    },
  });

  const iexQuote = await graphqlClient.query<
    GetIexQuote.Query,
    GetIexQuote.Variables
  >({
    query: GetIexQuote.Document,
    variables: {
      ticker: symbol,
    },
  });

  const otcTickers = [
    "XONI",
    "XFC",
    "DKMR",
    "APRN",
    "INND",
    "GMETF",
    "ACCYY",
    "MUSQ",
    "MOND",
  ];

  const isOtc = otcTickers.includes(symbol);

  if (iexCompany.data.iex.company === null && !isOtc) {
    return {
      redirect: {
        destination: "/404",
      },
      props: {
        iexCompany: {
          quote: iexQuote,
        },
      },
    };
  }

  const brandInsights = await client.getEntries<InsightDocument>(
    InsightsAssociatedWithBrand(symbol, [], "3")
  );

  // Load Generic Page for Brands

  const genericPage = await client.getEntries<GenericMarketingPage>({
    content_type: ContentfulContentType.GenericMarketingPage,
    ["fields.key"]: ContentfulKey.BrandDetail,
  });
  const page = genericPage.items.length > 0 ? genericPage.items[0] : null;
  const pageSections = compact(
    page?.fields.pageSections?.map((section) =>
      getItemReference(section, genericPage.includes)
    )
  );

  const joinNowCTA = pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.JoinNowCTA
  );
  const howItWorks = pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.HomeHowItWorks
  );
  const tiickerPerksCTA = pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.TiiCKERPerkCTA
  );

  const contentSections = await loadContentSections(client, pageSections);

  if (symbol === "XONI") {
    return {
      props: {
        symbol,
        iexCompany: {
          quote: iexQuote.data.iex.quote,
        },
        brandDetail,
        joinNowCTA,
        howItWorks,
        tiickerPerksCTA,
        redirected: redirect,
        pageSectionIncludes: genericPage.includes,
        contentSections,
        brandPerks,
        owningPerks,
        brandInsights,
        brandInvestment: matchingBrandInvestment || null,
      },
      revalidate: 100,
    };
  }

  return {
    props: {
      symbol,
      iexCompany: iexCompany.data.iex.company || null,
      brandDetail,
      joinNowCTA,
      howItWorks,
      tiickerPerksCTA,
      redirected: redirect,
      pageSectionIncludes: genericPage.includes,
      contentSections,
      brandPerks,
      owningPerks,
      brandInsights,
      brandInvestment: matchingBrandInvestment || null,
    },
    revalidate: 100,
  };
};

export default BrandDetailTabs;
